/**
 * React hook for fetching available models from the RAG server.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { getRagConnector } from '@/services/ragConnector'
import type { ModelInfo } from '@/services/ragConnector'
import { useRagProxyBase } from '@/hooks/useRagProxyBase'

interface UseModelsOptions {
  /** Initial server URL to fetch models from on mount (overrides global setting) */
  initialServerUrl?: string
  /** Initial API key to use when fetching models (overrides global setting) */
  initialApiKey?: string
  /** Whether to fetch models automatically on mount */
  autoFetch?: boolean
}

interface UseModelsReturn {
  /** Available models from the server */
  models: ModelInfo[]
  /** Whether models are currently being fetched */
  isLoading: boolean
  /** Error message if fetch failed */
  error: string | null
  /** Manually trigger a model fetch, optionally with a specific URL and API key */
  fetchModels: (serverUrl?: string, apiKey?: string) => Promise<void>
  /** The server URL that was last used for fetching */
  lastFetchedUrl: string | null
}

export function useModels(options: UseModelsOptions = {}): UseModelsReturn {
  const { initialServerUrl, initialApiKey, autoFetch = true } = options
  const ragProxyBase = useRagProxyBase()

  const [models, setModels] = useState<ModelInfo[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastFetchedUrl, setLastFetchedUrl] = useState<string | null>(null)

  // Track if we've done the initial fetch
  const hasFetchedRef = useRef(false)

  // Default URL: deployment-level RAG base (was connections.ragServerUrl).
  const defaultUrl = ragProxyBase

  const fetchModels = useCallback(async (serverUrl?: string, apiKey?: string) => {
    const urlToUse = serverUrl || defaultUrl

    setIsLoading(true)
    setError(null)

    try {
      const connector = getRagConnector()
      const response = await connector.fetchModelsFromUrl(urlToUse, apiKey)

      setModels(response.data || [])
      setLastFetchedUrl(urlToUse)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch models'
      setError(errorMessage)
      setModels([])
    } finally {
      setIsLoading(false)
    }
  }, [defaultUrl])

  // Auto-fetch only once on mount
  useEffect(() => {
    if (autoFetch && !hasFetchedRef.current) {
      hasFetchedRef.current = true
      const urlToUse = initialServerUrl || defaultUrl
      fetchModels(urlToUse, initialApiKey)
    }
  }, [autoFetch, initialServerUrl, initialApiKey, defaultUrl, fetchModels])

  return {
    models,
    isLoading,
    error,
    fetchModels,
    lastFetchedUrl,
  }
}
